import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	ResolveEnd,
	ResolveStart,
	Router
} from '@angular/router';
import { Observable, catchError, finalize, throwError } from 'rxjs';
import { LoadingService } from '@shared/components/ngx-loading/services/loading.service';
import { SKIP_GLOBAL_LOADING } from './skip-global-loading.token';

/**
 * Interface for tracking HTTP requests with their timing information
 */
interface RequestTracker {
	/** The URL of the request */
	url: string;
	/** Timestamp when the request started */
	startTime: number;
	/** Timestamp when the request completed (if finished) */
	endTime?: number;
}

/**
 * HTTP Interceptor that manages loading states during navigation and HTTP requests.
 * This interceptor shows and hides a loading indicator based on active HTTP requests
 * and navigation events. It also includes request timeout monitoring and URL exclusion
 * capabilities.
 *
 * Features:
 * - Automatic loading indicator management for HTTP requests
 * - Navigation event handling for route changes
 * - Request timeout monitoring
 * - URL exclusion support
 * - Request tracking and debugging capabilities
 */
@Injectable()
export class NavigationLoadingInterceptor implements HttpInterceptor {
	/** Router service injected using the new inject function */
	#router = inject(Router);
	/** Loading service injected using the new inject function */
	#loadingService = inject(LoadingService);

	/** Timeout threshold for requests in milliseconds */
	private readonly TIMEOUT = 30000;

	/** List of URLs that should not trigger the loading indicator */
	private readonly EXCLUDE_URLS: string[] = [
		'/health-check',
		'/ping'
		// Add more URLs to exclude
	];

	/** Array to track all requests and their timing information */
	private requestsTracker: RequestTracker[] = [];

	constructor() {
		this.handleRouteEvents();
		this.setupTimeoutCheck();
	}

	/**
	 * Intercepts HTTP requests to manage loading indicator state
	 * @param request The outgoing HTTP request
	 * @param next The HTTP handler for the request
	 * @returns An Observable of the HTTP event stream
	 */
	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (
			this.shouldExclude(request.url) ||
			request.context.get(SKIP_GLOBAL_LOADING)
		) {
			return next.handle(request);
		}

		this.startRequest(request.url);

		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				this.handleError(error);
				return throwError(() => error);
			}),
			finalize(() => {
				this.endRequest(request.url);
			})
		);
	}

	/**
	 * Starts tracking a new request and shows the loading indicator
	 * @param url The URL of the request to track
	 */
	private startRequest(url: string): void {
		this.requestsTracker.push({
			url,
			startTime: Date.now()
		});
		this.#loadingService.show();
	}

	/**
	 * Ends tracking of a request and hides the loading indicator
	 * @param url The URL of the completed request
	 */
	private endRequest(url: string): void {
		const request = this.requestsTracker.find((r) => r.url === url);
		if (request) {
			request.endTime = Date.now();
		}
		this.#loadingService.hide();
	}

	/**
	 * Sets up subscription to router events to handle loading states during navigation
	 */
	private handleRouteEvents(): void {
		this.#router.events.subscribe((event) => {
			if (event instanceof NavigationStart || event instanceof ResolveStart) {
				this.#loadingService.show();
			} else if (
				event instanceof NavigationEnd ||
				event instanceof NavigationCancel ||
				event instanceof NavigationError ||
				event instanceof ResolveEnd
			) {
				this.#loadingService.hide();
			}
		});
	}

	/**
	 * Checks if a URL should be excluded from loading indicator management
	 * @param url The URL to check
	 * @returns True if the URL should be excluded, false otherwise
	 */
	private shouldExclude(url: string): boolean {
		return this.EXCLUDE_URLS.some((excludeUrl) => url.includes(excludeUrl));
	}

	/**
	 * Sets up periodic cleanup of request tracking and handles timeouts
	 * Runs every second to:
	 * - Remove completed requests older than 1 minute
	 * - Handle requests that have exceeded the timeout threshold
	 */
	private setupTimeoutCheck(): void {
		setInterval(() => {
			const now = Date.now();
			this.requestsTracker = this.requestsTracker.filter((request) => {
				const elapsed = now - request.startTime;
				if (!request.endTime && elapsed > this.TIMEOUT) {
					console.warn(
						`Request to ${request.url} timed out after ${this.TIMEOUT}ms`
					);
					this.#loadingService.hide();
					return false;
				}
				return !request.endTime || elapsed < 60000; // Keep completed requests for 1 minute
			});
		}, 1000);
	}

	/**
	 * Handles HTTP error responses
	 * @param error The HTTP error response
	 */
	private handleError(error: HttpErrorResponse): void {
		console.error('Request error:', error);
	}

	/**
	 * Returns a list of currently active requests
	 * Useful for debugging and monitoring purposes
	 * @returns An array of active request trackers
	 */
	getActiveRequests(): RequestTracker[] {
		return this.requestsTracker.filter((r) => !r.endTime);
	}
}
